import { Link, useLocation } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import defaultAsset from '@r/assets/images/fiddy/default-product-image.png';
import { Card, CardContent, CardFooter, CardHeader } from '@ui/card.tsx';
import { useTranslation } from 'react-i18next';
import { Button } from '@ui/button.tsx';
import { Badge } from '@ui/badge.tsx';
import { FormattedHtml } from '@r/components/formatted-html.tsx';
import pointsTranslation from '@r/translations/points-translation.ts';
import type { ProductProductReadModel } from '@r/api/generated/client.schemas.ts';
import { ArrowTopRightIcon } from '@radix-ui/react-icons';
import { isOutOfStock } from '@r/utils/product-stock.ts';
import { cn } from '@r/utils/utils.ts';
import { ProductStockStatus } from './product-stock-status.tsx';

type Product = ProductProductReadModel;

const ProductCatalog = ({ shopId, product }: { shopId: number; product: Product }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const outOfStock = isOutOfStock(product.stock);

  const productUrl = {
    to: '/shops/$shopId/products/$id' as const,
    params: { shopId: shopId.toString(), id: product.id.toString() }
  };

  const ProductButton = ({ children, className }: { children: ReactNode; className?: string }) => (
    <Link {...productUrl} className={className} search={{ previousRoute: location.pathname }}>
      {children}
    </Link>
  );

  return (
    <ProductButton className="h-full">
      <Card
        className={cn(
          'h-full grid grid-cols-12 overflow-hidden',
          outOfStock && 'bg-muted/40 opacity-60'
        )}
      >
        <div className="col-span-4 flex items-center justify-center p-2 relative">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-l from-muted-foreground/10 to-transparent to-50%" />
          <img
            className="object-cover object-center w-full"
            src={product.images?.[0]?.pathUri || defaultAsset}
            alt={`${product.name} illustration`}
            onError={(e) => {
              e.currentTarget.src = defaultAsset;
              e.currentTarget.onerror = null;
            }}
          />
        </div>

        <div className="col-span-8 flex flex-col justify-between">
          <CardHeader className="space-y-2">
            {product.category && (
              <Badge className="w-fit font-medium">{product.category.label}</Badge>
            )}
            {product.brand && (
              <p className="text-xs font-medium text-foreground">{product.brand.name}</p>
            )}
            <FormattedHtml
              as="h1"
              text={product.name}
              className="text-xl font-medium line-clamp-2"
            />
            <FormattedHtml
              text={product.designationCom}
              className="text-xs text-muted-foreground/60 line-clamp-2"
            />
            <ProductStockStatus
              stock={product.stock}
              lowStockAlert={product.lowStockAlert}
              className="w-fit"
            />
          </CardHeader>

          <div>
            <CardContent>
              <FormattedHtml
                as="div"
                text={`${product.description ?? ''}${product.ean ? ` (EAN: ${product.ean})` : ''}`}
                className="text-muted-foreground line-clamp-2 text-sm"
              />
            </CardContent>

            <CardFooter className="gap-5 flex flex-wrap justify-between">
              {product.price ? (
                <p className="flex flex-wrap items-center gap-2">
                  <span className="text-xl font-semibold">{product.pointUnitPrice}</span>
                  <span className="text-xs font-medium">
                    {pointsTranslation(t, product.pointUnitPrice)}
                  </span>
                </p>
              ) : (
                <p>{t('shop.product.custom.amount')}</p>
              )}
              <Button rounded="full" size="icon" variant="outline">
                <ArrowTopRightIcon />
              </Button>
            </CardFooter>
          </div>
        </div>
      </Card>
    </ProductButton>
  );
};

export { ProductCatalog };
